import { rnd1 } from "../../Game";
import { Node } from "../../Utils/Collections/Node";
import { NodeList } from "../../Utils/Collections/NodeList";
import { NumValBase } from "../../Utils/NumberValue";
import { DefenderSettings } from "../Defenders/DefenderSettings";
import { TutorialSettings } from "../HUD/Tutorial";
import { TargetSettingsBase } from "../Targets/TargetSettings";


/**
 * Data pro nastaveni sceny
 */
export interface SceneSetupData {
    /**
     * Jedinecna identifikace nastaveni
     */
    uid: number;
    /**
     * Minimalni index vyberu polozek ze skupiny, pri kterem je mozne scenu pouzit.
     * Pokud nezadano, tak je scenu mozne pouzit uz pri prvnim vyberu
     */
    groupMinSelectionId?: number;
    /**
     * Vzdalenost od stredu brany.
     * Pokud neni zadano, tak se vzdalenost vypocita podle aktualni obtiznosti v rozmezi RoundSetup.MIN_KICKOFF_DIS az RoundSetup.MAX_KICKOFF_DIS
     */
    kickOffDis?: number | NumValBase;
    /**
     * Uhel od stredu brany.
     */
    kickOffAngle: number | NumValBase;
    /**
     * Pozice brankare v ramci brany (0 = uplne vlevo, 1 = uplne vravo).
     * Pokud nezadano, tak 0.5.
     * Pokud < jak 0 nebo > jak 1, tak se brankar vypne
     */
    goaliePos?: number | NumValBase;
    /**
     * Nastaveni terce nebo tercu v brane
     */
    targets: TargetSettingsBase | TargetSettingsBase[];
    /**
     * Pokud nastaveno na true, tak je mozne u hlavniho terce menit velikost (urci RoundSetup). Pokud nezadano, tak false.
     */
    enableMainTargetScaling?: boolean;
    /**
     * Pravdepodobnost zobrazeni bonusovych tercu (0 az 100). Pokud nezadano, tak se pouzije RoundSetup.BONUS_TARGETS_PROBABILITY
     */
    bonusTargetsProb?: number;
    /**
     * Nastaveni obrancu. Pokud nezadano, tak je scena bez obrancu
     */
    defenderSettings?: DefenderSettings;
    /**
     * Index zakladniho nastaveni obrancu. Pokud nezadano, tak se obranci nastavuji pres defenderSettings
     */
    defDefenderSettingsId?: number;
    /**
     * Zakazani nahodneho prevraceni strany. Pokud nezadano, tak je prevraceni povoleno
     */
    disableFlipSide?: boolean,
    /**
     * Nastaveni tutorialu. Pokud nezadano, tak tutorial neni.
     */
    tutorial?: TutorialSettings
}



/** 
 * Rezim vyberu
 */
export const enum eSelectionMode {
    inOrder,
    random
}



/**
 * Data skupiny s nastavenim scen
 */
export interface SceneSetupGroupData {
    /**
     * Jednotlive nastaveni scen
     */
    setups: SceneSetupData[];
    /**
     * Rezim vyberu
     */
    selectionMode: eSelectionMode;
}



/**
 * Data skupiny skupin s nastavenim scen
 */
export interface SceneSetupGroupBlockData {
    /**
     * Skupiny v bloku
     */
    groups: SceneSetupGroupData[];
    /**
     * Rezim vyberu
     */
    selectionMode: eSelectionMode;
    /**
     * Pocet nastaveni scen, ktere se maji vybrat. Pokud nezadano, nebo <= 0, tak se budou sceny vybirat donekonecna
     */
    sceneToSelect?: number;
}



class SceneSetupGroup {
    private static _tmpSceneNodeList: Node<SceneSetupData>[] = [];

    private _data: SceneSetupGroupData;
    private _remSetups: NodeList<SceneSetupData>;
    /**
     * (RO) Zbyvajici pocet nevybranych nastaveni scen
     */
    get remSetupCnt(): number { return this._remSetups.size; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._remSetups = new NodeList<SceneSetupData>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(data: SceneSetupGroupData): void {
        this._data = data;
        this.fillRemSetups();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getSceneSetup(roundId: number): SceneSetupData {
        if (this._remSetups.empty)
            this.fillRemSetups();

        // Vytvorit seznam s uzly scen, ktere je mozne pouzit
        let list = SceneSetupGroup._tmpSceneNodeList;
        let listSize = 0;

        this._remSetups.forEach((setup: SceneSetupData, node: Node<SceneSetupData>): void => {
            if (setup.groupMinSelectionId == undefined || setup.groupMinSelectionId <= roundId)
                list[listSize++] = node;
        });

        if (listSize == 0)
            return undefined;

        return this._remSetups.removeByNode(list[this._data.selectionMode == eSelectionMode.inOrder ? 0 : rnd1.randomIntIn(0, listSize - 1)]);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private fillRemSetups(): void {
        this._remSetups.clear();
        this._remSetups.fill(this._data.setups);
    }
}



export class SceneSetupSelector {
    private _data: SceneSetupGroupBlockData;
    /**
     * Vsechny skupiny
     */
    private _sceneGroups: SceneSetupGroup[];
    /**
     * Zbyvajici skupiny pro vyber
     */
    private _remSceneGroups: NodeList<SceneSetupGroup>;
    /**
     * (RO) Pocet zbyvajicich scen k vyberu
     */
    get remSceneCnt(): number { return this._remSceneCnt; }
    private _remSceneCnt: number;
    /**
     * Index vyberoveho kola scen. Navysi se po vyberu sceny z kazde skupiny v seznamu skupin
     */
    private _selRoundId: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._remSceneGroups = new NodeList<SceneSetupGroup>();
        this._sceneGroups = [];
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(blockData: SceneSetupGroupBlockData): void {
        this._data = blockData;
        this._remSceneCnt = blockData.sceneToSelect || -1;
        this._selRoundId = 0;

        // Vytvorit skupiny
        blockData.groups.forEach((groupData: SceneSetupGroupData, i: number): void => {
            let group: SceneSetupGroup;

            if (i < this._sceneGroups.length) {
                group = this._sceneGroups[i];
            }
            else {
                group = new SceneSetupGroup();
                this._sceneGroups.push(group);
            }

            group.init(groupData);
        });

        // Naplnit seznam zbyvajicich skupin
        this.fillRemSceneGroups();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getSceneSetup(): SceneSetupData {
        // Snizit pocet zbyvajicich scen v bloku
        if (this._remSceneCnt > 0)
            this._remSceneCnt--;

        let setup: SceneSetupData;

        while (!setup) {
            if (this._remSceneGroups.empty) {
                this._selRoundId++;
                this.fillRemSceneGroups();
            }

            setup = this._remSceneGroups.removeById(this._data.selectionMode == eSelectionMode.inOrder ? 0 : rnd1.randomIntIn(0, this._remSceneGroups.size - 1))
                .getSceneSetup(this._selRoundId);
        }

        return setup;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private fillRemSceneGroups(): void {
        this._remSceneGroups.clear();
        this._remSceneGroups.fill(this._sceneGroups);
    }
}